import { Badge, Group, Text } from '@mantine/core';
import { useHeartRhythmMeasurementDetails } from 'hooks/api/useHeartRhythmData';
import { useParameters } from 'hooks/useParameters';
import { HeartRhythmMeasurementStatus } from 'models/heartRhythm/measurement';
import { MeasurementInformationBadge } from 'models/measurement';
import { useTranslation } from 'react-i18next';
import { showUsaFeature } from 'utils/app';
import { formatDate } from 'utils/dates';
import {
  getHeartrateColor,
  getHeartrateLabel,
  getHeartRhythmIndicatorColor,
  getHeartRhythmIndicatorLabel,
  getUnreviewedMeasurementLabel,
} from 'utils/measurement';
import { Indicator } from 'utils/models';
import { queryParams } from 'utils/query';

export const MeasurementDetailsBadges = () => {
  const { t } = useTranslation();
  const { parameters: filters } = useParameters();
  const { data: selectedMeasurement } = useHeartRhythmMeasurementDetails({
    measurementId: filters[queryParams.detailId],
  });

  const renderBadge = ({ color, label, hideBadge }: MeasurementInformationBadge) => {
    if (hideBadge) return <></>;
    return (
      <Badge color={color} size={'lg'} radius={'sm'} variant={'filled'}>
        {label}
      </Badge>
    );
  };

  const renderDiagnosisBadgeUsa = () => {
    return renderBadge({
      color: getHeartRhythmIndicatorColor(selectedMeasurement?.data?.indicator),
      label: getHeartRhythmIndicatorLabel(selectedMeasurement?.data?.indicator),
    });
  };

  const renderDiagnosisBadge = () => {
    if (showUsaFeature) return renderDiagnosisBadgeUsa();

    if (!selectedMeasurement?.data?.diagnosis) return null;

    const diagnosisLabel =
      selectedMeasurement?.status === HeartRhythmMeasurementStatus.Reviewed
        ? t(`diagnosis_${selectedMeasurement?.data.diagnosis?.label[0]}`)
        : getUnreviewedMeasurementLabel(selectedMeasurement?.data?.indicator);

    return renderBadge({
      color: getHeartRhythmIndicatorColor(selectedMeasurement?.data?.indicator),
      label: diagnosisLabel,
    });
  };
  return (
    <>
      <Group mb={'xs'}>
        {renderDiagnosisBadge()}
        {renderBadge({
          color: getHeartrateColor(selectedMeasurement?.data?.heartrate),
          label: getHeartrateLabel(selectedMeasurement?.data?.heartrate),
          hideBadge:
            (showUsaFeature && selectedMeasurement?.data?.indicator === Indicator.Quality) ||
            (selectedMeasurement?.data?.heartrate ?? 0) <= 0,
        })}
      </Group>
      <Group>
        <Text size={'sm'} mb={'xl'} fs={'italic'}>
          {formatDate({ timestamp: selectedMeasurement?.data?.measurement_timestamp || 0, formatWithTime: true })}
        </Text>
        {selectedMeasurement?.status === HeartRhythmMeasurementStatus.Reviewed &&
          selectedMeasurement.updateTimestamp && (
            <Text size={'sm'} mb={'xl'} fs={'italic'}>
              {`(${t('patients_measurement_detail_reviewed_on', {
                value: `${formatDate({ timestamp: new Date(selectedMeasurement?.updateTimestamp), formatWithTime: true })})`,
              })}`}
            </Text>
          )}
      </Group>
    </>
  );
};
